import type { BloodTest, BloodTestCategory } from '@careflux/mercury';
import { type BloodTestSelection, type LabTest, bloodTestResolutionSchema } from './bloodTests.types';

// Sameind group names (yfirflokkur) → Mercury workspace tabs.
const GROUP_TO_CATEGORY: Record<string, BloodTestCategory> = {
  'klínísk lífefna- og blóðmeinafr.': 'clinical-biochemistry',
  'sýkla- og veirufr.': 'microbiology-virology',
  'ónæmisfr.': 'immunology',
  'ofnæmisfr.': 'allergology',
  annað: 'other',
};

/** Unknown or missing groups land on the default (first) tab so they stay visible. */
export const resolveCategory = (group: string | undefined): BloodTestCategory => {
  const key = group?.trim().toLowerCase();
  if (!key) return 'clinical-biochemistry';
  return GROUP_TO_CATEGORY[key] ?? 'clinical-biochemistry';
};

/** Mercury `id` is the lab test code (what gets ordered); Mercury shows `code` as the chip label. */
export const toBloodTest = (test: LabTest): BloodTest => ({
  id: test.code,
  code: (test.displayName ?? test.name).trim(),
  name: test.displayName ?? test.name,
  category: resolveCategory(test.group),
  price: test.price ?? 0,
});

const GENERAL = ['blóðhagur / deilit.', 'blóðstatus', 'natríum', 'kalíum', 'kreatínín'];
// "Almennar blóðprufur" is snakk plus CRP.
const GENERAL_WITH_CRP = [...GENERAL, 'crp'];
const CHOLESTEROL = ['kólesteról', 'ldl-kólesteról', 'hdl-kólesteról', 'þríglyceríðar'];
const DIABETES = ['hba1c mmól/mól', 'glúkósi'];
const THYROID = ['tsh', 'ft4'];
const LIVER = ['asat (got)', 'alat (gpt)', 'alp', 'gamma-gt', 'bílírúbín'];
const KIDNEY = ['kreatínín', 'úrea', 'natríum', 'kalíum'];
const ELECTROLYTES = ['natríum', 'kalíum', 'klóríð'];
const IRON = ['fe + tibc', 'ferritin'];
const THYROID_ANTIBODIES = ['tpo mótefni', 'tg mótefni'];
const RHEUMATOLOGY = ['rf skimpróf', 'ccp-mótefni', 'ana skimpróf', 'crp', 'sökk'];

// AI-detected abbreviations/categories → lowercase test names (or codes) they stand for.
const TERM_ALIASES: Record<string, string[]> = {
  tg: ['þríglyceríðar'],
  hdl: ['hdl-kólesteról'],
  ldl: ['ldl-kólesteról'],
  kolesterol: ['kólesteról'],
  blóðfita: CHOLESTEROL,
  blóðfitur: CHOLESTEROL,
  blóðfitu: CHOLESTEROL,
  na: ['natríum'],
  k: ['kalíum'],
  sölt: ELECTROLYTES,
  blóðsölt: ELECTROLYTES,
  blóðsöltum: ELECTROLYTES,
  elektrólýtar: ELECTROLYTES,
  krea: ['kreatínín'],
  crea: ['kreatínín'],
  status: ['blóðhagur / deilit.', 'blóðstatus'],
  blóðhagur: ['blóðhagur / deilit.', 'blóðstatus'],
  // Detection writes the inflected forms ("panta blóðhag", "niðurstöður blóðhags").
  blóðhag: ['blóðhagur / deilit.', 'blóðstatus'],
  blóðhags: ['blóðhagur / deilit.', 'blóðstatus'],
  almennar: GENERAL_WITH_CRP,
  almenn: GENERAL_WITH_CRP,
  almennt: GENERAL_WITH_CRP,
  snakk: GENERAL,
  sykursýkis: DIABETES,
  sykursýki: DIABETES,
  sykur: DIABETES,
  hba1c: ['hba1c mmól/mól'],
  langtímasykur: ['hba1c mmól/mól'],
  langtímasykurs: ['hba1c mmól/mól'],
  glukosi: ['glúkósi'],
  blóðsykur: ['glúkósi'],
  'fastandi blóðsykur': ['glúkósi'],
  'd-vítamín': ['25-oh-vítamín d'],
  'd vítamín': ['25-oh-vítamín d'],
  'd-vítamíns': ['25-oh-vítamín d'],
  'd vítamíns': ['25-oh-vítamín d'],
  hiv: ['hiv i og ii mótefni'],
  þvagprufa: ['þvagrannsókn'],
  þvagstix: ['þvagrannsókn'],
  kalk: ['kalsíum'],
  fólínsýra: ['fólat'],
  járn: IRON,
  járnbúskapur: IRON,
  járnbirgðir: IRON,
  'lifrarbólga b': ['hepatitis b (hbsag)'],
  'hep b': ['hepatitis b (hbsag)'],
  skjaldkirtill: THYROID,
  skjaldkirtilsmótefni: THYROID_ANTIBODIES,
  t3: ['ft3'],
  t4: ['ft4'],
  got: ['asat (got)'],
  gpt: ['alat (gpt)'],
  ggt: ['gamma-gt'],
};

// Accent-insensitive, so "Ferritín" in a todo matches Sameind's "Ferritin". Þ/ð/æ/ö have no
// combining mark and are unaffected.
const foldAccents = (value: string): string => value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

const normalizeTerm = (term: string): string =>
  foldAccents(
    term
      .trim()
      .toLowerCase()
      .replace(/^(s|þ)-/, '')
  );

const normalizedAliases = new Map(
  Object.entries(TERM_ALIASES).map(([term, targets]) => [normalizeTerm(term), targets])
);

const splitTerms = (text: string | null | undefined): string[] => [
  ...new Set(
    (text ?? '')
      .split(/[,;\n]/)
      .map((term) => term.trim())
      .filter(Boolean)
  ),
];

// Dictated lists end "ALAT og þvagrannsókn"; split there unless the whole term names a test ("HIV I og II mótefni").
const LIST_JOINER = /\s+og\s+/i;

/** A test's labels, each also without a trailing qualifier, so "ASAT" matches "ASAT (GOT)". */
const labelsOf = (test: LabTest): string[] =>
  [test.code, test.name, test.displayName]
    .filter((label): label is string => Boolean(label))
    .flatMap((label) => {
      const full = normalizeTerm(label);
      const base = full.replace(/\s*\([^)]*\)$/, '');
      return base && base !== full ? [full, base] : [full];
    });

// A panel stem plus a test word, as one compound or two words ("skjaldkirtilsblóðprufur", "lifrar próf"),
// tolerating dropped letters like "skjalkirtis-". Matched on normalized terms, so ó/í/ý are already folded.
const PANEL_WORD = String.raw`\s*(?:bloð|blod)?(?:pruf|prof|rannsokn)\S*`;
const panel = (stem: string, targets: string[]) => ({ pattern: new RegExp(`^${stem}${PANEL_WORD}$`), targets });
const PANELS = [
  panel('almenn(?:ar|a|t)?', GENERAL_WITH_CRP),
  panel('kolesterol', CHOLESTEROL),
  panel('sykursyki?s?', DIABETES),
  panel('skjal?d?kirti(?:l|ls|s)?', THYROID),
  panel('lifra?r?', LIVER),
  panel('bloðfitu', CHOLESTEROL),
  panel('nyrna', KIDNEY),
  panel('jarn', IRON),
  panel('gigtar', RHEUMATOLOGY),
];

/** An exact catalog label wins over aliases, so a Sameind test named "T3" isn't redirected to FT3. */
const aliasTargets = (term: string, catalogLabels: Set<string>): string[] => {
  const normalized = normalizeTerm(term);
  if (catalogLabels.has(normalized)) return [normalized];
  const targets = normalizedAliases.get(normalized) ??
    PANELS.find(({ pattern }) => pattern.test(normalized))?.targets ?? [term];
  return targets.map(normalizeTerm);
};

const testsNamedBy = (tests: LabTest[], terms: string[]): LabTest[] => {
  const catalogLabels = new Set(tests.flatMap(labelsOf));
  const wanted = new Set(terms.flatMap((term) => aliasTargets(term, catalogLabels)));
  return tests.filter((test) => labelsOf(test).some((label) => wanted.has(label)));
};

const requestedTerms = (tests: LabTest[], text: string | null | undefined): string[] =>
  splitTerms(text).flatMap((term) =>
    testsNamedBy(tests, [term]).length > 0
      ? [term]
      : term
          .split(LIST_JOINER)
          .map((part) => part.trim())
          .filter(Boolean)
  );

/** Codes of catalog tests named by the todo text (comma/semicolon/newline or "og" separated). */
export const matchTestCodes = (tests: LabTest[], text: string | null | undefined): string[] =>
  testsNamedBy(tests, requestedTerms(tests, text)).map((test) => test.code);

/** Matches each term on its own, so terms with no catalog test stay visible as unresolved. */
const matchTerms = (tests: LabTest[], terms: string[]): BloodTestSelection => {
  const matched = terms.map((term) => [term, matchTestCodes(tests, term)] as const);
  return {
    testCodes: [...new Set(matched.flatMap(([, codes]) => codes))],
    unresolvedTerms: matched.filter(([, codes]) => codes.length === 0).map(([term]) => term),
  };
};

/**
 * Saved catalogue IDs for the current description. Terms the resolver left unresolved (e.g. panels like
 * "skjaldkirtilsprufur") go through the alias table, and a match with a code no longer in the catalogue
 * is flagged instead of silently dropped.
 */
export const resolveTaskTestSelection = (
  tests: LabTest[],
  text: string | null | undefined,
  extraData?: Record<string, unknown> | null
): BloodTestSelection => {
  const parsed = bloodTestResolutionSchema.safeParse(extraData?.blood_test_resolution);
  if (!parsed.success || parsed.data.source_details !== text) return matchTerms(tests, requestedTerms(tests, text));
  const catalogueCodes = new Set(tests.map((test) => test.code));
  const isCurrent = (match: { test_codes: string[] }) => match.test_codes.every((code) => catalogueCodes.has(code));
  const { matches, unresolved_terms } = parsed.data;
  const expanded = matchTerms(tests, unresolved_terms);
  return {
    testCodes: [...new Set([...matches.filter(isCurrent).flatMap((match) => match.test_codes), ...expanded.testCodes])],
    unresolvedTerms: [
      ...new Set([
        ...expanded.unresolvedTerms,
        ...matches.filter((match) => !isCurrent(match)).map((match) => match.source_text),
      ]),
    ],
  };
};
